import { useRouter } from "next/router";
import qs from "qs";
import type { FunctionComponent, ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useReducer, useState } from "react";

import { MaximumArticleCardNumber } from "~/components/magazines-style/consts";
import { section } from "~/data";
import { getAbsoluteUrl } from "~/lib/utils";

import {
  articleCardInfoReducer,
  getArticleCardInfoActionType,
  initialArticleCardState,
} from "./helpers";
import type { ArticleCardState, GetArticleCardInfoActionTypeParameters } from "./helpers";
import { AdCardIndexSet } from "./story-style-layout/consts";

type ContextValue = {
  articleCardState?: ArticleCardState;
  closeRedirectUrl?: string;
  currentPageIndex: number;
  handleGoToNextPage: () => void;
  handleGoToPreviousPage: () => void;
  maximumCardIndex: number;
  totalArticleCount: number;
};
export const StoryStyleContext = createContext<ContextValue>({
  closeRedirectUrl: "",
  currentPageIndex: 0,
  handleGoToNextPage: () => {},
  handleGoToPreviousPage: () => {},
  maximumCardIndex: 0,
  totalArticleCount: 0,
});

export type Props = {
  children: ReactNode;
  hasAdCards?: boolean;
  totalArticleCount: number;
};

export const StoryStyleContextProvider: FunctionComponent<Props> = ({
  children,
  hasAdCards = false,
  totalArticleCount,
}) => {
  const maximumCardIndex = useMemo(
    () => (totalArticleCount ?? MaximumArticleCardNumber) + (hasAdCards ? AdCardIndexSet.size : 0),
    [hasAdCards, totalArticleCount],
  );
  const [articleCardState, dispatch] = useReducer(articleCardInfoReducer, initialArticleCardState);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const handleIndexChange = useCallback(
    ({
      currentPageIndex,
      updateType,
    }: {
      currentPageIndex: number;
      updateType: GetArticleCardInfoActionTypeParameters["updateType"];
    }) => {
      const updatedIndex = updateType === "increase" ? currentPageIndex + 1 : currentPageIndex - 1;
      setCurrentPageIndex(updatedIndex);
      const type = getArticleCardInfoActionType({
        currentIndex: currentPageIndex,
        hasAdCards,
        updateType,
      });
      dispatch({ type });
    },
    [hasAdCards],
  );

  const handleGoToNextPage = useCallback(() => {
    if (currentPageIndex === maximumCardIndex) {
      return;
    }
    handleIndexChange({ currentPageIndex, updateType: "increase" });
  }, [currentPageIndex, handleIndexChange, maximumCardIndex]);

  const handleGoToPreviousPage = useCallback(() => {
    if (currentPageIndex === 0) {
      return;
    }
    handleIndexChange({ currentPageIndex, updateType: "decrease" });
  }, [currentPageIndex, handleIndexChange]);

  const { asPath } = useRouter();
  const closeRedirectUrl = useMemo(() => {
    const parsedUrl = new URL(getAbsoluteUrl(asPath));
    const { origin = "" } = qs.parse(parsedUrl.search, {
      ignoreQueryPrefix: true,
    }) as { origin?: string };

    if (!origin) return `${section.style.urlAlias}`;
    return origin;
  }, [asPath]);

  return (
    <StoryStyleContext.Provider
      value={{
        articleCardState,
        closeRedirectUrl,
        currentPageIndex,
        handleGoToNextPage,
        handleGoToPreviousPage,
        maximumCardIndex,
        totalArticleCount,
      }}
    >
      {children}
    </StoryStyleContext.Provider>
  );
};

StoryStyleContextProvider.displayName = "StoryStyleContextProvider";

export const useStoryStyleContext = () => useContext(StoryStyleContext);
